import React, { useState, useRef, useEffect } from "react";
import { Trash2, RotateCcw, Plus, Eye, EyeOff } from "lucide-react";

const LineDivisionApp = () => {
  const [lines, setLines] = useState([]);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedLineIndex, setSelectedLineIndex] = useState(-1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState(null);
  const [showRegionLabels, setShowRegionLabels] = useState(false);
  const [regions, setRegions] = useState([]);
  const svgRef = useRef(null);

  const SVG_WIDTH = 600;
  const SVG_HEIGHT = 400;
  const ASPECT_RATIO = SVG_WIDTH / SVG_HEIGHT; // 3:2 比例

  // 扩展线段到画布边界
  const extendLineToCanvas = (line) => {
    const { x1, y1, x2, y2 } = line;
    const dx = x2 - x1;
    const dy = y2 - y1;

    if (Math.abs(dx) < 1e-10) {
      // 垂直线
      return { x1, y1: 0, x2, y2: SVG_HEIGHT };
    }

    if (Math.abs(dy) < 1e-10) {
      // 水平线
      return { x1: 0, y1, x2: SVG_WIDTH, y2 };
    }

    // 计算与画布边界的交点
    const slope = dy / dx;
    const b = y1 - slope * x1;

    const intersections = [];

    // 与左边界交点 (x = 0)
    const yAtLeft = b;
    if (yAtLeft >= 0 && yAtLeft <= SVG_HEIGHT) {
      intersections.push({ x: 0, y: yAtLeft });
    }

    // 与右边界交点 (x = SVG_WIDTH)
    const yAtRight = slope * SVG_WIDTH + b;
    if (yAtRight >= 0 && yAtRight <= SVG_HEIGHT) {
      intersections.push({ x: SVG_WIDTH, y: yAtRight });
    }

    // 与上边界交点 (y = 0)
    const xAtTop = -b / slope;
    if (xAtTop >= 0 && xAtTop <= SVG_WIDTH) {
      intersections.push({ x: xAtTop, y: 0 });
    }

    // 与下边界交点 (y = SVG_HEIGHT)
    const xAtBottom = (SVG_HEIGHT - b) / slope;
    if (xAtBottom >= 0 && xAtBottom <= SVG_WIDTH) {
      intersections.push({ x: xAtBottom, y: SVG_HEIGHT });
    }

    // 返回两个最远的交点
    if (intersections.length >= 2) {
      return {
        x1: intersections[0].x,
        y1: intersections[0].y,
        x2: intersections[intersections.length - 1].x,
        y2: intersections[intersections.length - 1].y,
      };
    }

    return line;
  };

  // 判断点在直线的哪一侧
  const getPointSide = (point, line) => {
    const { x, y } = point;
    const { x1, y1, x2, y2 } = line;
    return (x2 - x1) * (y - y1) - (y2 - y1) * (x - x1);
  };

  // 计算区域并生成标签
  const calculateRegions = () => {
    if (lines.length === 0) {
      setRegions([
        { x: SVG_WIDTH / 2, y: SVG_HEIGHT / 2, id: 1, size: "large" },
      ]);
      return;
    }

    const extendedLines = lines.map(extendLineToCanvas);
    const regionMap = new Map();
    const fineGridSize = 10; // 更密集的网格来检测小区域

    // 使用更密集的网格采样方法找到不同的区域
    for (let x = fineGridSize / 2; x < SVG_WIDTH; x += fineGridSize) {
      for (let y = fineGridSize / 2; y < SVG_HEIGHT; y += fineGridSize) {
        const point = { x, y };

        // 计算这个点相对于所有直线的位置编码
        const signature = extendedLines
          .map((line) => (getPointSide(point, line) > 0 ? "1" : "0"))
          .join("");

        if (!regionMap.has(signature)) {
          regionMap.set(signature, {
            points: [],
            signature,
            id: regionMap.size + 1,
          });
        }

        regionMap.get(signature).points.push(point);
      }
    }

    // 为每个区域计算中心点和大小
    const newRegions = Array.from(regionMap.values()).map((region) => {
      const points = region.points;
      const pointCount = points.length;

      // 计算区域中心点（所有采样点的平均值）
      const centerX = points.reduce((sum, p) => sum + p.x, 0) / pointCount;
      const centerY = points.reduce((sum, p) => sum + p.y, 0) / pointCount;

      // 根据采样点数量判断区域大小
      let size = "large";
      let radius = 15;
      let fontSize = 12;

      if (pointCount < 20) {
        size = "tiny";
        radius = 8;
        fontSize = 8;
      } else if (pointCount < 50) {
        size = "small";
        radius = 10;
        fontSize = 9;
      } else if (pointCount < 100) {
        size = "medium";
        radius = 12;
        fontSize = 10;
      }

      // 尝试找到距离所有直线最远的位置作为标签位置
      let bestPoint = { x: centerX, y: centerY };
      let maxDistance = 0;

      const searchRadius = Math.min(30, Math.sqrt(pointCount) * 2);
      for (let dx = -searchRadius; dx <= searchRadius; dx += 5) {
        for (let dy = -searchRadius; dy <= searchRadius; dy += 5) {
          const testPoint = {
            x: Math.max(radius, Math.min(SVG_WIDTH - radius, centerX + dx)),
            y: Math.max(radius, Math.min(SVG_HEIGHT - radius, centerY + dy)),
          };

          // 检查是否还在同一个区域
          const testSignature = extendedLines
            .map((line) => (getPointSide(testPoint, line) > 0 ? "1" : "0"))
            .join("");

          if (testSignature === region.signature) {
            // 计算到所有直线的最小距离
            let minDistance = Infinity;
            for (const line of extendedLines) {
              const distance =
                Math.abs(getPointSide(testPoint, line)) /
                Math.sqrt(
                  Math.pow(line.x2 - line.x1, 2) +
                    Math.pow(line.y2 - line.y1, 2)
                );
              minDistance = Math.min(minDistance, distance);
            }

            if (minDistance > maxDistance) {
              maxDistance = minDistance;
              bestPoint = testPoint;
            }
          }
        }
      }

      return {
        ...region,
        x: bestPoint.x,
        y: bestPoint.y,
        size,
        radius,
        fontSize,
        pointCount,
      };
    });

    setRegions(newRegions);
  };

  // 计算实际的区域数
  const calculateActualRegions = () => {
    if (lines.length === 0) return 1;

    const extendedLines = lines.map(extendLineToCanvas);
    const signatures = new Set();
    const gridSize = 10; // 与calculateRegions保持一致的网格密度

    for (let x = gridSize / 2; x < SVG_WIDTH; x += gridSize) {
      for (let y = gridSize / 2; y < SVG_HEIGHT; y += gridSize) {
        const point = { x, y };
        const signature = extendedLines
          .map((line) => (getPointSide(point, line) > 0 ? "1" : "0"))
          .join("");
        signatures.add(signature);
      }
    }

    return signatures.size;
  };

  // 获取SVG坐标
  const getSVGPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = SVG_WIDTH / rect.width;
    const scaleY = SVG_HEIGHT / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // 处理SVG点击
  const handleSVGClick = (e) => {
    if (isDragging) return;

    const point = getSVGPoint(e);

    if (isDrawing) {
      if (currentPoints.length === 0) {
        setCurrentPoints([point]);
      } else {
        const newLine = {
          x1: currentPoints[0].x,
          y1: currentPoints[0].y,
          x2: point.x,
          y2: point.y,
          id: Date.now(),
        };
        setLines([...lines, newLine]);
        setCurrentPoints([]); // 保持绘制模式，准备绘制下一条直线
      }
    } else {
      // 点击空白处取消选中
      setSelectedLineIndex(-1);
    }
  };

  // 处理直线点击
  const handleLineClick = (index, e) => {
    e.stopPropagation();
    // 只有在非绘制模式下才能选中直线
    if (!isDrawing) {
      setSelectedLineIndex(selectedLineIndex === index ? -1 : index);
    }
  };

  // 处理鼠标移动
  const handleMouseMove = (e) => {
    if (isDragging && selectedLineIndex >= 0 && dragPoint) {
      const point = getSVGPoint(e);
      const newLines = [...lines];
      if (dragPoint === "start") {
        newLines[selectedLineIndex].x1 = point.x;
        newLines[selectedLineIndex].y1 = point.y;
      } else {
        newLines[selectedLineIndex].x2 = point.x;
        newLines[selectedLineIndex].y2 = point.y;
      }
      setLines(newLines);
    }
  };

  // 处理鼠标抬起
  const handleMouseUp = () => {
    setIsDragging(false);
    setDragPoint(null);
  };

  // 开始拖拽端点
  const startDrag = (lineIndex, point, e) => {
    e.stopPropagation();
    setSelectedLineIndex(lineIndex);
    setDragPoint(point);
    setIsDragging(true);
  };

  // 删除直线
  const deleteLine = (index) => {
    const newLines = lines.filter((_, i) => i !== index);
    setLines(newLines);
    setSelectedLineIndex(-1);
  };

  // 清空所有直线
  const clearAll = () => {
    setLines([]);
    setCurrentPoints([]);
    setIsDrawing(false);
    setSelectedLineIndex(-1);
    setRegions([]);
  };

  // 开始绘制模式
  const startDrawing = () => {
    setIsDrawing(true);
    setCurrentPoints([]);
    setSelectedLineIndex(-1);
  };

  // 取消绘制
  const cancelDrawing = () => {
    setIsDrawing(false);
    setCurrentPoints([]);
    setSelectedLineIndex(-1); // 取消绘制时也取消选中状态
  };

  // 切换区域标签显示
  const toggleRegionLabels = () => {
    if (!showRegionLabels) {
      calculateRegions();
    }
    setShowRegionLabels(!showRegionLabels);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        cancelDrawing();
        setSelectedLineIndex(-1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 当直线改变时重新计算区域
  useEffect(() => {
    if (showRegionLabels) {
      calculateRegions();
    }
  }, [lines, showRegionLabels]);

  return (
    <div className="w-full max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          直线分割平面探索工具
        </h1>
        <p className="text-gray-600">
          点击"添加直线"后，在画布上点击两点来绘制直线，探索直线如何分割平面
        </p>
      </div>

      {/* 控制面板 */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={startDrawing}
          disabled={isDrawing}
          className={`flex items-center gap-2 px-4 py-2 rounded ${
            isDrawing
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          <Plus size={16} />
          {isDrawing ? "连续添加模式" : "开始添加直线"}
        </button>

        {isDrawing && (
          <button
            onClick={cancelDrawing}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            完成添加 (ESC)
          </button>
        )}

        <button
          onClick={toggleRegionLabels}
          className={`flex items-center gap-2 px-4 py-2 rounded ${
            showRegionLabels
              ? "bg-green-500 text-white hover:bg-green-600"
              : "bg-gray-500 text-white hover:bg-gray-600"
          }`}
        >
          {showRegionLabels ? <Eye size={16} /> : <EyeOff size={16} />}
          {showRegionLabels ? "隐藏区域标号" : "显示区域标号"}
        </button>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          <RotateCcw size={16} />
          清空所有
        </button>
      </div>

      {/* 统计信息 */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {lines.length}
            </div>
            <div className="text-sm text-gray-600">直线数量</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {calculateActualRegions()}
            </div>
            <div className="text-sm text-gray-600">区域数量</div>
          </div>
        </div>
      </div>

      {/* SVG画布 */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          className="w-full h-auto bg-yellow-50 cursor-crosshair"
          style={{ aspectRatio: `${ASPECT_RATIO}` }}
          onClick={handleSVGClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {/* 网格背景 */}
          <defs>
            <pattern
              id="grid"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* 绘制所有直线 */}
          {lines.map((line, index) => {
            const extended = extendLineToCanvas(line);
            const isSelected = selectedLineIndex === index;
            return (
              <g key={line.id}>
                {/* 扩展到边界的直线 */}
                <line
                  x1={extended.x1}
                  y1={extended.y1}
                  x2={extended.x2}
                  y2={extended.y2}
                  stroke={isSelected ? "#ef4444" : "#dc2626"}
                  strokeWidth={isSelected ? "3" : "2"}
                  onClick={(e) => handleLineClick(index, e)}
                  className="cursor-pointer"
                />

                {/* 只在选中时显示端点控制器 */}
                {isSelected && (
                  <>
                    <circle
                      cx={line.x1}
                      cy={line.y1}
                      r="6"
                      fill="#3b82f6"
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-move"
                      onMouseDown={(e) => startDrag(index, "start", e)}
                    />
                    <circle
                      cx={line.x2}
                      cy={line.y2}
                      r="6"
                      fill="#3b82f6"
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-move"
                      onMouseDown={(e) => startDrag(index, "end", e)}
                    />
                  </>
                )}
              </g>
            );
          })}

          {/* 当前绘制的点 */}
          {currentPoints.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="4"
              fill="#10b981"
              stroke="white"
              strokeWidth="2"
            />
          ))}

          {/* 区域标号 */}
          {showRegionLabels &&
            regions.map((region) => (
              <g key={region.id}>
                <circle
                  cx={region.x}
                  cy={region.y}
                  r={region.radius}
                  fill="rgba(99, 102, 241, 0.8)"
                  stroke="white"
                  strokeWidth={region.size === "tiny" ? "1" : "2"}
                />
                <text
                  x={region.x}
                  y={region.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize={region.fontSize}
                  fontWeight="bold"
                >
                  {region.id}
                </text>
              </g>
            ))}

          {/* 绘制状态提示 */}
          {isDrawing && (
            <text x="10" y="25" fill="#059669" fontSize="14" fontWeight="bold">
              {currentPoints.length === 0
                ? "连续添加模式：点击画布绘制直线起点"
                : "点击第二个点完成直线绘制"}
            </text>
          )}
        </svg>
      </div>

      {/* 直线列表 */}
      {lines.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">已绘制的直线</h3>
          <div className="space-y-2">
            {lines.map((line, index) => (
              <div
                key={line.id}
                className={`flex items-center justify-between p-3 border rounded ${
                  selectedLineIndex === index
                    ? "border-red-500 bg-red-50"
                    : "border-gray-200"
                }`}
              >
                <span className="text-sm">
                  直线 {index + 1}: ({Math.round(line.x1)},{" "}
                  {Math.round(line.y1)}) → ({Math.round(line.x2)},{" "}
                  {Math.round(line.y2)})
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setSelectedLineIndex(
                        selectedLineIndex === index ? -1 : index
                      )
                    }
                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {selectedLineIndex === index ? "取消选择" : "选择"}
                  </button>
                  <button
                    onClick={() => deleteLine(index)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <Trash2 size={12} />
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default LineDivisionApp;
